import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { X, Volume2, VolumeX, ExternalLink } from 'lucide-react';
import { adsApi } from '@/lib/api';
import { consume as consumeAdBudget, remaining as remainingAdBudget } from '@/lib/adBudget';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

function getCreativeUrl(url) {
  if (!url) return '';
  if (url.startsWith('http')) return url;
  if (url.startsWith('/uploads/')) return `${BACKEND_URL}${url}`;
  // Object storage path — serve via /api/files/ endpoint
  return `${BACKEND_URL}/api/files/${url}`;
}

// ===== BANNER AD =====
export function AdBanner({ ad, onClose, onImgError }) {
  const [imgError, setImgError] = useState(false);
  const handleClick = () => {
    adsApi.trackClick(ad.id).catch(() => {});
    if (ad.destination_url) window.open(ad.destination_url, '_blank', 'noopener');
  };

  useEffect(() => {
    adsApi.trackImpression(ad.id).catch(() => {});
  }, [ad.id]);

  if (imgError) return null;

  return (
    <div className="relative group rounded-lg overflow-hidden bg-gray-50 border border-gray-100" data-testid={`ad-banner-${ad.id}`}>
      <button
        onClick={onClose}
        className="absolute top-2 right-2 z-10 w-6 h-6 bg-black/50 rounded-full flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity"
        data-testid="ad-close"
      >
        <X className="w-3.5 h-3.5" />
      </button>
      <div className="text-[10px] text-gray-400 absolute top-2 left-2 bg-white/80 px-1.5 py-0.5 rounded">Ad</div>
      <div onClick={handleClick} className="cursor-pointer">
        <img
          src={getCreativeUrl(ad.creative_url)}
          alt={ad.alt_text || ad.name}
          className="w-full h-auto max-h-[280px] object-cover"
          loading="lazy"
          onError={() => { setImgError(true); if (onImgError) onImgError(); }}
        />
      </div>
    </div>
  );
}

// ===== TEXT AD =====
export function AdText({ ad, onClose }) {
  const handleClick = () => {
    adsApi.trackClick(ad.id).catch(() => {});
    if (ad.cta_url) window.open(ad.cta_url, '_blank', 'noopener');
  };

  useEffect(() => {
    adsApi.trackImpression(ad.id).catch(() => {});
  }, [ad.id]);

  return (
    <div className="relative bg-gradient-to-r from-gray-50 to-white border border-gray-100 rounded-lg p-4 sm:p-5" data-testid={`ad-text-${ad.id}`}>
      <button
        onClick={onClose}
        className="absolute top-2 right-2 w-5 h-5 bg-gray-200/80 rounded-full flex items-center justify-center text-gray-500 hover:bg-gray-300 transition-colors"
        data-testid="ad-close"
      >
        <X className="w-3 h-3" />
      </button>
      <div className="text-[10px] text-gray-400 mb-1.5">Sponsored</div>
      {ad.headline && <h4 className="font-semibold text-gray-900 text-sm sm:text-base mb-1">{ad.headline}</h4>}
      {ad.body_text && <p className="text-gray-600 text-xs sm:text-sm mb-3 leading-relaxed">{ad.body_text}</p>}
      {ad.cta_text && (
        <button
          onClick={handleClick}
          className="inline-flex items-center gap-1.5 text-xs font-medium bg-gray-900 text-white px-4 py-2 rounded-lg hover:bg-gray-800 transition-colors"
        >
          {ad.cta_text}
          <ExternalLink className="w-3 h-3" />
        </button>
      )}
    </div>
  );
}

// ===== VIDEO AD PLAYER =====
export function AdVideoPlayer({ ad, onClose, onComplete }) {
  const videoRef = useRef(null);
  const [timeLeft, setTimeLeft] = useState(ad.duration || 15);
  const [canSkip, setCanSkip] = useState(false);
  const [muted, setMuted] = useState(true);
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    adsApi.trackImpression(ad.id).catch(() => {});
    const skipTimer = setTimeout(() => setCanSkip(true), 5000);
    return () => clearTimeout(skipTimer);
  }, [ad.id]);

  useEffect(() => {
    if (!playing) return;
    if (timeLeft <= 0) {
      onComplete?.();
      return;
    }
    const timer = setInterval(() => setTimeLeft(t => t - 1), 1000);
    return () => clearInterval(timer);
  }, [playing, timeLeft, onComplete]);

  const handlePlay = () => {
    videoRef.current?.play();
    setPlaying(true);
  };

  const handleClick = () => {
    adsApi.trackClick(ad.id).catch(() => {});
    if (ad.destination_url) window.open(ad.destination_url, '_blank', 'noopener');
  };

  return (
    <div className="relative bg-black rounded-lg overflow-hidden" data-testid={`ad-video-${ad.id}`}>
      <div className="text-[10px] text-white/60 absolute top-2 left-2 z-10 bg-black/50 px-1.5 py-0.5 rounded">Ad</div>
      
      {/* Progress bar */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-white/20 z-10">
        <div
          className="h-full bg-white/80 transition-all duration-1000 ease-linear"
          style={{ width: `${((ad.duration || 15) - timeLeft) / (ad.duration || 15) * 100}%` }}
        />
      </div>

      {/* Controls overlay */}
      <div className="absolute bottom-0 left-0 right-0 flex items-center justify-between px-3 py-2 bg-gradient-to-t from-black/60 z-10">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setMuted(!muted)}
            className="text-white/80 hover:text-white"
            data-testid="ad-video-mute"
          >
            {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
          </button>
          <span className="text-xs text-white/60">{timeLeft}s</span>
        </div>
        <div className="flex items-center gap-2">
          {ad.destination_url && (
            <button onClick={handleClick} className="text-[10px] text-white/70 hover:text-white flex items-center gap-1">
              Learn more <ExternalLink className="w-3 h-3" />
            </button>
          )}
          {canSkip ? (
            <button
              onClick={() => { onClose(); onComplete?.(); }}
              className="text-xs bg-white/20 hover:bg-white/30 text-white px-3 py-1 rounded transition-colors"
              data-testid="ad-video-skip"
            >
              Skip Ad
            </button>
          ) : (
            <span className="text-[10px] text-white/50">Skip in {Math.max(0, 5 - ((ad.duration || 15) - timeLeft))}s</span>
          )}
        </div>
      </div>

      <video
        ref={videoRef}
        src={getCreativeUrl(ad.video_url)}
        muted={muted}
        autoPlay
        playsInline
        onPlay={() => setPlaying(true)}
        onEnded={() => onComplete?.()}
        className="w-full max-h-[300px] object-contain cursor-pointer"
        onClick={handleClick}
      />
      
      {!playing && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/40 cursor-pointer" onClick={handlePlay}>
          <div className="w-14 h-14 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm">
            <div className="w-0 h-0 border-t-[10px] border-t-transparent border-l-[16px] border-l-white border-b-[10px] border-b-transparent ml-1" />
          </div>
        </div>
      )}
    </div>
  );
}

// ===== ROADBLOCK AD =====
export function AdRoadblock({ ad, onDismiss }) {
  const [countdown, setCountdown] = useState(ad.roadblock_duration || 5);
  const [canClose, setCanClose] = useState(false);

  useEffect(() => {
    adsApi.trackImpression(ad.id).catch(() => {});
  }, [ad.id]);

  useEffect(() => {
    if (countdown <= 0) {
      setCanClose(true);
      return;
    }
    const timer = setInterval(() => setCountdown(c => c - 1), 1000);
    return () => clearInterval(timer);
  }, [countdown]);

  const handleClick = () => {
    adsApi.trackClick(ad.id).catch(() => {});
    if (ad.destination_url) window.open(ad.destination_url, '_blank', 'noopener');
  };

  return (
    <div className="fixed inset-0 z-[10000] bg-black/80 flex items-center justify-center p-4" data-testid={`ad-roadblock-${ad.id}`}>
      <div className="relative bg-white rounded-2xl overflow-hidden max-w-lg w-full shadow-2xl">
        {/* Top bar */}
        <div className="flex items-center justify-between px-4 py-2.5 bg-gray-50 border-b border-gray-100">
          <span className="text-[10px] text-gray-400">Sponsored</span>
          {canClose ? (
            <button
              onClick={onDismiss}
              className="text-xs flex items-center gap-1 text-gray-500 hover:text-gray-700 font-medium"
              data-testid="roadblock-close"
            >
              <X className="w-3.5 h-3.5" /> Close
            </button>
          ) : (
            <span className="text-xs text-gray-400">Closing in {countdown}s</span>
          )}
        </div>
        
        {/* Creative */}
        <div onClick={handleClick} className="cursor-pointer">
          {ad.creative_url && (
            <img
              src={getCreativeUrl(ad.creative_url)}
              alt={ad.alt_text || ad.name}
              className="w-full max-h-[400px] object-cover"
            />
          )}
          {ad.headline && (
            <div className="p-5 text-center">
              <h3 className="font-bold text-lg text-gray-900 mb-2">{ad.headline}</h3>
              {ad.body_text && <p className="text-gray-600 text-sm mb-4">{ad.body_text}</p>}
              {ad.cta_text && (
                <span className="inline-flex items-center gap-1.5 bg-gray-900 text-white px-6 py-2.5 rounded-lg font-medium text-sm">
                  {ad.cta_text} <ExternalLink className="w-3.5 h-3.5" />
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ===== AD SLOT (Container that fetches & renders ads) =====
export default function AdSlot({ site = 'auto', placement = 'hero_after', maxAds = 2, className = '' }) {
  const [ads, setAds] = useState([]);
  const [dismissed, setDismissed] = useState(new Set());
  const [failedImages, setFailedImages] = useState(new Set());
  const [videoCompleted, setVideoCompleted] = useState(new Set());
  const [loaded, setLoaded] = useState(false);
  // How many slots this AdSlot instance has claimed from the global per-page budget.
  // Claimed atomically once at mount so we know upfront how many creatives can render.
  const budgetRef = useRef(null);
  if (budgetRef.current === null) {
    budgetRef.current = Math.min(maxAds, remainingAdBudget());
    if (budgetRef.current > 0) consumeAdBudget(budgetRef.current);
  }
  const budgetAllowed = budgetRef.current;

  useEffect(() => {
    if (budgetAllowed === 0) { setLoaded(true); return; }
    adsApi.serve(site, placement, 5).then(res => {
      setAds(res.data.ads || []);
    }).catch(() => {}).finally(() => setLoaded(true));
  }, [site, placement, budgetAllowed]);

  const dismissAd = (adId) => setDismissed(prev => new Set([...prev, adId]));
  const markFailed = (adId) => setFailedImages(prev => new Set([...prev, adId]));

  const visibleAds = ads.filter(a => !dismissed.has(a.id) && !failedImages.has(a.id) && a.ad_type !== 'roadblock').slice(0, budgetAllowed);

  // Budget already exhausted globally — render nothing (silent overflow).
  if (budgetAllowed === 0) return null;

  // Show "Advertise Here" when no valid ads — but never on Vikasifications or DGTG (premium B2B sites, no ad slots)
  if (loaded && visibleAds.length === 0) {
    if (site === 'vikas' || site === 'dgtg') return null;

    const contactPath = site === 'devmob' ? '/devmob/contact' : '/contact';
    return (
      <div className={`${className} py-3`} data-testid={`ad-slot-${placement}`}>
        <div className="max-w-5xl mx-auto px-4">
          <Link to={contactPath} state={{ source: 'ad-slot', site, placement }} className="block bg-gray-50 border border-gray-200 rounded-xl py-5 px-6 text-center hover:border-gray-400 hover:shadow-md transition-all cursor-pointer group" data-testid={`advertise-here-${placement}`}>
            <p className="text-sm font-bold text-gray-500 uppercase tracking-widest group-hover:text-gray-700">Advertise With Us</p>
            <p className="text-xs text-gray-400 mt-1">Reach our audience, click here to get started</p>
          </Link>
        </div>
      </div>
    );
  }

  if (!loaded) return null;

  return (
    <div className={`space-y-4 ${className}`} data-testid={`ad-slot-${placement}`}>
      {visibleAds.map(ad => {
        if (ad.ad_type === 'banner') return <AdBanner key={ad.id} ad={ad} onClose={() => dismissAd(ad.id)} onImgError={() => markFailed(ad.id)} />;
        if (ad.ad_type === 'text') return <AdText key={ad.id} ad={ad} onClose={() => dismissAd(ad.id)} />;
        if (ad.ad_type === 'video') return (
          <AdVideoPlayer key={ad.id} ad={ad} onClose={() => dismissAd(ad.id)} onComplete={() => setVideoCompleted(prev => new Set([...prev, ad.id]))} />
        );
        return null;
      })}
    </div>
  );
}
